'use client';

import {
  Box,
  Flex,
  Highlight,
  Icon,
  Image,
  Input,
  useBreakpointValue,
  InputGroup,
  Text,
} from '@chakra-ui/react';

import { LuSearch, LuX } from 'react-icons/lu';

import { useCallback, useEffect, useState } from 'react';
import { api } from '@/services/api';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

let timeoutId: NodeJS.Timeout;

const SearchBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<
    {
      id: string;
      name: string;
      model: string;
      slug: string;
      brand: {
        id: string;
        name: string;
      };
      images: {
        id: string;
        image: string;
        label: string;
        alt: string;
        path: string;
      }[];
    }[]
  >([]);

  const value = useBreakpointValue({ base: 'mobile', md: 'desktop' });
  const router = useRouter();

  const handleSubmitSearch = useCallback(() => {
    if (!searchTerm.trim()) return;
    setIsOpen(false);
    setProducts([]);
    router.push(`/pesquisa?q=${encodeURIComponent(searchTerm.trim())}`);
  }, [searchTerm, router]);

  const handleGetSearchResults = useCallback(async (query: string) => {
    try {
      const response = await api.get(`/products/search?query=${query}`);

      setProducts(response.data);

      if (response.data.length === 0) setError(true);
      else setError(false);
    } catch {
      setError(true);
    }
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    if (timeoutId) clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      if (newValue.trim() === '') {
        setProducts([]);
        setError(false);
        return;
      }
      handleGetSearchResults(newValue);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (value === 'desktop') {
      setIsOpen(false);
    }
  }, [value]);

  return (
    <>
      {value === 'desktop' ? (
        <Flex w="100%" pl="0" pr="0" flexDir="column" position="relative">
          <InputGroup flex="1" startElement={<LuSearch color="rgba(0,176,202,0.7)" />}>
            <Input
              placeholder="Pesquisar produtos..."
              fontSize="14px"
              borderRadius="full"
              width="full"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmitSearch();
              }}
              bg={{ base: 'rgba(0,0,0,0.02)', _dark: 'rgba(255,255,255,0.04)' }}
              border="1px solid"
              color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
              borderColor={{ base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.1)' }}
              _placeholder={{ color: 'rgba(0,0,0,0.28)', _dark: { color: 'rgba(255,255,255,0.3)' } }}
              _hover={{ borderColor: 'rgba(0,176,202,0.4)', bg: { base: '#FFFFFF', _dark: 'rgba(255,255,255,0.06)' } }}
              _focus={{ borderColor: '#00B0CA', boxShadow: '0 0 0 3px rgba(0,176,202,0.1)', bg: { base: '#FFFFFF', _dark: 'rgba(255,255,255,0.06)' }, outline: 'none' }}
              _active={{ boxShadow: 'none' }}
              h="40px"
            />
          </InputGroup>
          {products.length > 0 && (
            <Flex
              flexDir="column"
              position="absolute"
              zIndex="10"
              w="100%"
              borderRadius="16px"
              maxH="400px"
              overflowY="auto"
              overflowX="hidden"
              top="48px"
              left="0"
              bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
              boxShadow="0 12px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
              border="1px solid"
              borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.07)' }}
              p="6px"
            >
              {products.map((product, index) => {
                const par = index % 2 === 0;

                return (
                  <Box key={product.id}>
                    <Link href={`/produto/${product.slug}`}>
                      <Flex
                        align="center"
                        gap="12px"
                        px="10px"
                        py="8px"
                        borderRadius="12px"
                        _hover={{ bg: 'rgba(0,176,202,0.06)' }}
                        transition="all 0.15s"
                      >
                        <Flex
                          w="56px"
                          h="56px"
                          minW="56px"
                          align="center"
                          justify="center"
                          bg={{ base: 'rgba(0,0,0,0.03)', _dark: 'rgba(255,255,255,0.05)' }}
                          borderRadius="10px"
                          overflow="hidden"
                          flexShrink={0}
                        >
                          <Image
                            src={
                              product.images[0]
                                ? product.images[0].path
                                : '/logo-idu.svg'
                            }
                            alt="image"
                            w="auto"
                            h="44px"
                            objectFit="contain"
                          />
                        </Flex>
                        <Flex flexDir="column" gap="2px" flex="1" minW="0">
                          <Text
                            fontSize="13px"
                            fontWeight="600"
                            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
                            lineHeight="1.3"
                          >
                            <Highlight
                              query={searchTerm}
                              ignoreCase
                              styles={{ color: '#00B0CA', backgroundColor: 'rgba(0,176,202,0.1)', borderRadius: '3px' }}
                            >
                              {product.name}
                            </Highlight>
                          </Text>
                          <Text
                            fontSize="12px"
                            color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}
                            lineHeight="1.3"
                          >
                            <Highlight query={searchTerm} ignoreCase styles={{ color: '#00B0CA' }}>
                              {product.brand.name}
                            </Highlight>
                            {' · '}
                            <Highlight query={searchTerm} ignoreCase styles={{ color: '#00B0CA' }}>
                              {product.model}
                            </Highlight>
                          </Text>
                        </Flex>
                      </Flex>
                    </Link>

                    {par ? (
                      <Box
                        h="1px"
                        bg={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.05)' }}
                        mx="10px"
                      />
                    ) : null}
                  </Box>
                );
              })}
              <Flex
                as="button"
                onClick={handleSubmitSearch}
                align="center"
                justify="center"
                py="10px"
                mt="4px"
                borderRadius="10px"
                cursor="pointer"
                _hover={{ bg: 'rgba(0,176,202,0.06)' }}
              >
                <Text fontSize="12.5px" fontWeight="600" color="#00B0CA">
                  Ver todos os resultados para &quot;{searchTerm}&quot;
                </Text>
              </Flex>
            </Flex>
          )}
          {error && (
            <Flex
              position="absolute"
              zIndex="10"
              w="100%"
              top="48px"
              left="0"
              align="center"
              justify="center"
              gap="8px"
              py="16px"
              bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
              borderRadius="16px"
              border="1px solid"
              borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.07)' }}
              boxShadow="0 8px 24px rgba(0,0,0,0.1)"
            >
              <Icon w="15px" h="15px" color={{ base: 'rgba(0,0,0,0.3)', _dark: 'rgba(255,255,255,0.3)' }}>
                <LuSearch />
              </Icon>
              <Text fontSize="13px" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.4)' }}>
                Sem resultados para{' '}
                <Text as="span" fontWeight="600" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                  “{searchTerm}”
                </Text>
              </Text>
            </Flex>
          )}
        </Flex>
      ) : (
        <Flex
          as="button"
          aria-label="Abrir pesquisa"
          aria-expanded={isOpen}
          w="38px"
          h="38px"
          borderRadius="11px"
          bg={{ base: 'rgba(0,176,202,0.08)', _dark: 'rgba(0,176,202,0.12)' }}
          border="1.5px solid"
          borderColor={{ base: 'rgba(0,176,202,0.2)', _dark: 'rgba(0,176,202,0.25)' }}
          align="center"
          justify="center"
          cursor="pointer"
          flexShrink={0}
          transition="all 0.18s"
          _hover={{ bg: 'rgba(0,176,202,0.15)', borderColor: '#00B0CA' }}
          _active={{ transform: 'scale(0.93)' }}
          onClick={() => {
            setIsOpen((prevState) => !prevState);
            if (isOpen) {
              setProducts([]);
              setError(false);
              setSearchTerm('');
            }
          }}
        >
          <Icon w="18px" h="18px" color="#00B0CA">
            <LuSearch />
          </Icon>
        </Flex>
      )}

      {isOpen && (
        <>
          {/* Backdrop */}
          <Box
            pos="fixed"
            inset="0"
            zIndex="40"
            bg="rgba(0,0,0,0.5)"
            style={{ backdropFilter: 'blur(4px)' }}
            onClick={() => {
              setIsOpen(false);
              setProducts([]);
              setError(false);
              setSearchTerm('');
            }}
          />

          {/* Search panel — slides from top */}
          <Box
            pos="fixed"
            top="0"
            left="0"
            right="0"
            zIndex="50"
            bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
            borderBottomRadius="24px"
            boxShadow="0 12px 48px rgba(0,0,0,0.22), 0 2px 8px rgba(0,176,202,0.1)"
            border="2px solid"
            borderTop="none"
            borderColor={{ base: 'rgba(0,176,202,0.14)', _dark: 'rgba(0,176,202,0.12)' }}
            px="16px"
            pt="20px"
            pb="20px"
          >

            {/* Input + close */}
            <Flex gap="10px" align="center">
              <InputGroup flex="1" minW="0" startElement={<LuSearch color="rgba(0,176,202,0.75)" />}>
                <Input
                  placeholder="Pesquisar produtos..."
                  fontSize="16px"
                  borderRadius="full"
                  width="full"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmitSearch();
                  }}
                  autoFocus
                  bg={{ base: 'rgba(0,0,0,0.03)', _dark: 'rgba(255,255,255,0.05)' }}
                  border="1.5px solid"
                  color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
                  borderColor={{ base: 'rgba(0,176,202,0.22)', _dark: 'rgba(0,176,202,0.18)' }}
                  _placeholder={{ color: 'rgba(0,0,0,0.32)', _dark: { color: 'rgba(255,255,255,0.32)' } }}
                  _focus={{ borderColor: '#00B0CA', boxShadow: '0 0 0 3px rgba(0,176,202,0.12)', outline: 'none' }}
                  h="46px"
                />
              </InputGroup>
              <Flex
                as="button"
                aria-label="Fechar pesquisa"
                w="46px"
                h="46px"
                borderRadius="full"
                align="center"
                justify="center"
                bg={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.08)' }}
                flexShrink={0}
                cursor="pointer"
                transition="all 0.18s"
                _hover={{ bg: { base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.13)' } }}
                onClick={() => {
                  setIsOpen(false);
                  setProducts([]);
                  setError(false);
                  setSearchTerm('');
                }}
              >
                <Icon w="18px" h="18px" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.5)' }}>
                  <LuX />
                </Icon>
              </Flex>
            </Flex>

            {/* Results */}
            {products.length > 0 && (
              <Flex flexDir="column" maxH="60vh" overflowY="auto" overflowX="hidden" mt="12px" gap="2px">
                {products.map((product, index) => (
                  <Box key={product.id}>
                    <Link
                      href={`/produto/${product.slug}`}
                      onClick={() => { setIsOpen(false); setProducts([]); setSearchTerm(''); }}
                    >
                      <Flex
                        align="center"
                        gap="12px"
                        px="10px"
                        py="10px"
                        borderRadius="14px"
                        _hover={{ bg: 'rgba(0,176,202,0.07)' }}
                        transition="all 0.15s"
                      >
                        <Flex
                          w="54px" h="54px" minW="54px"
                          align="center" justify="center"
                          bg={{ base: 'rgba(0,0,0,0.04)', _dark: 'rgba(255,255,255,0.05)' }}
                          borderRadius="12px"
                          overflow="hidden"
                          flexShrink={0}
                          border="1px solid"
                          borderColor={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.06)' }}
                        >
                          <Image
                            src={product.images[0] ? product.images[0].path : '/logo-idu.svg'}
                            alt="image"
                            w="auto" h="42px"
                            objectFit="contain"
                          />
                        </Flex>
                        <Flex flexDir="column" gap="2px" flex="1" minW="0">
                          <Text fontSize="13px" fontWeight="600" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }} lineHeight="1.3">
                            <Highlight query={searchTerm} ignoreCase styles={{ color: '#00B0CA', backgroundColor: 'rgba(0,176,202,0.1)', borderRadius: '3px' }}>
                              {product.name}
                            </Highlight>
                          </Text>
                          <Text fontSize="12px" color={{ base: 'rgba(0,0,0,0.48)', _dark: 'rgba(255,255,255,0.42)' }} lineHeight="1.3">
                            <Highlight query={searchTerm} ignoreCase styles={{ color: '#00B0CA' }}>{product.brand.name}</Highlight>
                            {' · '}
                            <Highlight query={searchTerm} ignoreCase styles={{ color: '#00B0CA' }}>{product.model}</Highlight>
                          </Text>
                        </Flex>
                      </Flex>
                    </Link>
                    {index < products.length - 1 && (
                      <Box h="1px" bg={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.05)' }} mx="10px" />
                    )}
                  </Box>
                ))}
                <Flex
                  as="button"
                  onClick={handleSubmitSearch}
                  align="center"
                  justify="center"
                  py="12px"
                  mt="4px"
                  borderRadius="12px"
                  cursor="pointer"
                  _hover={{ bg: 'rgba(0,176,202,0.07)' }}
                >
                  <Text fontSize="13px" fontWeight="600" color="#00B0CA">
                    Ver todos os resultados para &quot;{searchTerm}&quot;
                  </Text>
                </Flex>
              </Flex>
            )}

            {/* No results */}
            {error && (
              <Flex align="center" justify="center" gap="8px" py="20px" mt="8px">
                <Icon w="16px" h="16px" color={{ base: 'rgba(0,0,0,0.28)', _dark: 'rgba(255,255,255,0.28)' }}>
                  <LuSearch />
                </Icon>
                <Text fontSize="13px" color={{ base: 'rgba(0,0,0,0.48)', _dark: 'rgba(255,255,255,0.4)' }}>
                  Sem resultados para{' '}
                  <Text as="span" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                    &quot;{searchTerm}&quot;
                  </Text>
                </Text>
              </Flex>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default SearchBar;
